/**
 * Item Categories Settings Page
 *
 * Allows workspace admins to:
 * - Toggle built-in item categories (problem, opportunity, etc.) on/off
 * - Add custom categories
 * - Edit the 3-question altitude quiz for any category (builtin or custom)
 */

import { useEffect, useState } from 'react';
import {
  LayoutGrid,
  Plus,
  ChevronDown,
  ChevronRight,
  Trash2,
  Check,
  X,
  Tag,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useItemCategoriesStore } from '../../../stores/prioritization/itemCategoriesStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import type { WorkspaceItemCategory, WorkspaceCategoryQuestion } from '../../../lib/itemCategoriesService';

// ============================================================================
// Question Editor
// ============================================================================

const DIMENSIONS: Array<{ key: WorkspaceCategoryQuestion['dimension']; label: string }> = [
  { key: 'timeHorizon',    label: 'Time Horizon' },
  { key: 'workSize',       label: 'Work Size / Effort' },
  { key: 'impactSurface',  label: 'Impact Surface' },
];

interface QuestionEditorProps {
  categoryId: string;
  categorySlug: string;
}

function QuestionEditor({ categoryId, categorySlug }: QuestionEditorProps) {
  const { questions, fetchQuestionsForCategory, saveQuestion } = useItemCategoriesStore();
  const addNotification = useUIStore((s) => s.addNotification);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    fetchQuestionsForCategory(categoryId);
  }, [categoryId, fetchQuestionsForCategory]);

  const catQuestions = questions[categoryId] ?? [];

  const handleSave = async (
    dimension: WorkspaceCategoryQuestion['dimension'],
    draft: Omit<WorkspaceCategoryQuestion, 'id' | 'categoryId'>
  ) => {
    setSaving(dimension);
    await saveQuestion(categoryId, draft);
    setSaving(null);
    addNotification({ type: 'success', title: 'Question saved', message: '', duration: 2000 });
  };

  return (
    <div className="space-y-4 pl-4 border-l-2 border-[var(--rule)]">
      <p className="text-xs text-[var(--text-ter)]">
        Edit the 3 altitude-leveling questions for <strong>{categorySlug}</strong>. Each question has 5 answer options
        (1 = tactical, 5 = strategic).
      </p>
      {DIMENSIONS.map(({ key, label }) => {
        const existing = catQuestions.find((q) => q.dimension === key);
        return (
          <DimensionQuestionEditor
            key={key}
            dimension={key}
            dimensionLabel={label}
            existing={existing}
            onSave={(draft) => handleSave(key, draft)}
            saving={saving === key}
          />
        );
      })}
    </div>
  );
}

interface DimensionQuestionEditorProps {
  dimension: WorkspaceCategoryQuestion['dimension'];
  dimensionLabel: string;
  existing?: WorkspaceCategoryQuestion;
  onSave: (draft: Omit<WorkspaceCategoryQuestion, 'id' | 'categoryId'>) => Promise<void>;
  saving: boolean;
}

function DimensionQuestionEditor({
  dimension,
  dimensionLabel,
  existing,
  onSave,
  saving,
}: DimensionQuestionEditorProps) {
  const [questionText, setQuestionText] = useState(existing?.questionText ?? '');
  const [option1, setOption1] = useState(existing?.option1 ?? '');
  const [option2, setOption2] = useState(existing?.option2 ?? '');
  const [option3, setOption3] = useState(existing?.option3 ?? '');
  const [option4, setOption4] = useState(existing?.option4 ?? '');
  const [option5, setOption5] = useState(existing?.option5 ?? '');

  useEffect(() => {
    if (existing) {
      setQuestionText(existing.questionText);
      setOption1(existing.option1);
      setOption2(existing.option2);
      setOption3(existing.option3);
      setOption4(existing.option4);
      setOption5(existing.option5);
    }
  }, [existing]);

  const isDirty =
    questionText !== (existing?.questionText ?? '') ||
    option1 !== (existing?.option1 ?? '') ||
    option2 !== (existing?.option2 ?? '') ||
    option3 !== (existing?.option3 ?? '') ||
    option4 !== (existing?.option4 ?? '') ||
    option5 !== (existing?.option5 ?? '');

  const canSave = questionText.trim() && option1.trim() && option2.trim() && option3.trim() && option4.trim() && option5.trim();

  return (
    <div className="p-3 bg-[var(--surface)] border border-[var(--rule)] space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">{dimensionLabel}</h5>
        {isDirty && canSave && (
          <button
            onClick={() =>
              onSave({ dimension, questionText, option1, option2, option3, option4, option5 })
            }
            disabled={saving}
            className="text-xs px-2.5 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
      <div>
        <label className="block text-xs text-[var(--text-sec)] mb-1">Question</label>
        <input
          type="text"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="e.g. When would this deliver value?"
          className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-400 focus:outline-none"
        />
      </div>
      <div className="grid grid-cols-5 gap-2">
        {[
          { val: option1, set: setOption1, num: 1 },
          { val: option2, set: setOption2, num: 2 },
          { val: option3, set: setOption3, num: 3 },
          { val: option4, set: setOption4, num: 4 },
          { val: option5, set: setOption5, num: 5 },
        ].map(({ val, set, num }) => (
          <div key={num}>
            <label className="block text-xs text-[var(--text-ter)] mb-1 text-center">{num}</label>
            <input
              type="text"
              value={val}
              onChange={(e) => set(e.target.value)}
              placeholder={`Option ${num}`}
              className="w-full px-2 py-1 text-xs border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-400 focus:outline-none text-center"
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-[var(--text-ter)]">Option 1 = most tactical, Option 5 = most strategic</p>
    </div>
  );
}

// ============================================================================
// Altitude Labels Editor
// ============================================================================

const ALTITUDE_LEVELS = [5, 4, 3, 2, 1] as const;
type AltLevel = typeof ALTITUDE_LEVELS[number];

interface AltitudeLabelsEditorProps {
  categoryId: string;
  categorySlug: string;
  currentLabels: Record<AltLevel, string | null>;
  onSave: (labels: Record<AltLevel, string | null>) => Promise<void>;
}

function AltitudeLabelsEditor({ categorySlug, currentLabels, onSave }: AltitudeLabelsEditorProps) {
  const addNotification = useUIStore((s) => s.addNotification);
  const [labels, setLabels] = useState<Record<AltLevel, string>>({
    5: currentLabels[5] ?? '',
    4: currentLabels[4] ?? '',
    3: currentLabels[3] ?? '',
    2: currentLabels[2] ?? '',
    1: currentLabels[1] ?? '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLabels({
      5: currentLabels[5] ?? '',
      4: currentLabels[4] ?? '',
      3: currentLabels[3] ?? '',
      2: currentLabels[2] ?? '',
      1: currentLabels[1] ?? '',
    });
  }, [currentLabels]);

  const isDirty = ALTITUDE_LEVELS.some((lvl) => labels[lvl] !== (currentLabels[lvl] ?? ''));

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      5: labels[5].trim() || null,
      4: labels[4].trim() || null,
      3: labels[3].trim() || null,
      2: labels[2].trim() || null,
      1: labels[1].trim() || null,
    });
    setSaving(false);
    addNotification({ type: 'success', title: 'Labels saved', message: '', duration: 2000 });
  };

  return (
    <div className="space-y-3 pl-4 border-l-2 border-[var(--rule)]">
      <p className="text-xs text-[var(--text-ter)]">
        Define the 5 type names for <strong>{categorySlug}</strong> that appear in the Item Types grid.
        Leave blank to show a placeholder. Altitude 5 = most strategic, Altitude 1 = most tactical.
      </p>
      <div className="grid grid-cols-5 gap-2">
        {ALTITUDE_LEVELS.map((lvl) => (
          <div key={lvl}>
            <label className="block text-xs text-[var(--text-ter)] mb-1 text-center font-medium">Level {lvl}</label>
            <input
              type="text"
              value={labels[lvl]}
              onChange={(e) => setLabels((prev) => ({ ...prev, [lvl]: e.target.value }))}
              placeholder={`Alt ${lvl}`}
              className="w-full px-2 py-1 text-xs border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-400 focus:outline-none text-center"
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-[var(--text-ter)]">Level 5 = most strategic · Level 1 = most tactical</p>
      {isDirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-xs px-2.5 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save labels'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Category Row
// ============================================================================

interface CategoryRowProps {
  category: WorkspaceItemCategory;
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  onSaveLabels: (id: string, labels: Record<AltLevel, string | null>) => Promise<void>;
}

function CategoryRow({ category, onToggle, onDelete, onSaveLabels }: CategoryRowProps) {
  const [expanded, setExpanded] = useState<'quiz' | 'labels' | null>(null);

  return (
    <div className={` border transition-colors ${category.isActive ? 'bg-[var(--bg-raised)] border-[var(--rule)]' : 'bg-[var(--surface)] border-[var(--rule)] opacity-60'}`}>
      <div className="flex items-center gap-3 p-3">
        {/* Color swatch */}
        <span
          className="flex-shrink-0 w-3 h-3 rounded-full"
          style={{ backgroundColor: category.color ?? '#94a3b8' }}
        />

        {/* Active toggle */}
        <button
          onClick={() => onToggle(category.id, !category.isActive)}
          className={`flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            category.isActive ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
          }`}
          role="switch"
          aria-checked={category.isActive}
          title={category.isActive ? 'Deactivate' : 'Activate'}
        >
          <span
            className={`block w-4 h-4 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${
              category.isActive ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>

        {/* Label + slug */}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-[var(--text-pri)]">{category.label}</span>
          <span className="ml-2 text-xs text-[var(--text-ter)] font-mono">{category.slug}</span>
          {category.description && (
            <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{category.description}</p>
          )}
        </div>

        {/* Builtin badge */}
        {category.isBuiltin && (
          <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] rounded-full">
            builtin
          </span>
        )}

        {/* Expand labels editor */}
        <button
          onClick={() => setExpanded((e) => (e === 'labels' ? null : 'labels'))}
          className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
            expanded === 'labels'
              ? 'text-primary-700 bg-primary-50'
              : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
          }`}
          title="Edit altitude labels"
        >
          {expanded === 'labels' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <Tag className="h-3 w-3" />
          Labels
        </button>

        {/* Expand quiz editor */}
        <button
          onClick={() => setExpanded((e) => (e === 'quiz' ? null : 'quiz'))}
          className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
            expanded === 'quiz'
              ? 'text-primary-700 bg-primary-50'
              : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
          }`}
          title="Edit altitude questions"
        >
          {expanded === 'quiz' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Quiz
        </button>

        {/* Delete (custom only) */}
        {!category.isBuiltin && (
          <button
            onClick={() => onDelete(category.id)}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] rounded hover:bg-[var(--coral-dim)]"
            title="Delete custom category"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {expanded === 'labels' && (
        <div className="px-3 pb-3">
          <AltitudeLabelsEditor
            categoryId={category.id}
            categorySlug={category.slug}
            currentLabels={category.altitudeLabels}
            onSave={(labels) => onSaveLabels(category.id, labels)}
          />
        </div>
      )}

      {expanded === 'quiz' && (
        <div className="px-3 pb-3">
          <QuestionEditor categoryId={category.id} categorySlug={category.slug} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Add Category Form
// ============================================================================

interface AddCategoryFormProps {
  workspaceId: string;
  onAdd: (
    workspaceId: string,
    label: string,
    color: string | null,
    description: string | null,
    altitudeLabels: Record<AltLevel, string | null>
  ) => Promise<WorkspaceItemCategory | null>;
}

const COLOR_PRESETS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#10b981', '#3b82f6', '#8b5cf6', '#ec4899',
];

const EMPTY_ALTITUDE_LABELS: Record<AltLevel, string> = { 5: '', 4: '', 3: '', 2: '', 1: '' };

function AddCategoryForm({ workspaceId, onAdd }: AddCategoryFormProps) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_PRESETS[5]!);
  const [altLabels, setAltLabels] = useState<Record<AltLevel, string>>(EMPTY_ALTITUDE_LABELS);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setLabel('');
    setDescription('');
    setColor(COLOR_PRESETS[5]!);
    setAltLabels(EMPTY_ALTITUDE_LABELS);
  };

  const handleAdd = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    await onAdd(workspaceId, trimmed, color, description.trim() || null, {
      5: altLabels[5].trim() || null,
      4: altLabels[4].trim() || null,
      3: altLabels[3].trim() || null,
      2: altLabels[2].trim() || null,
      1: altLabels[1].trim() || null,
    });
    setSaving(false);
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 hover:bg-primary-50 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add custom category
      </button>
    );
  }

  return (
    <div className="p-4 border border-primary-300 bg-primary-50 space-y-3">
      <h4 className="text-sm font-semibold text-[var(--text-pri)]">New Category</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Name *</label>
          <input
            autoFocus
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
            placeholder="e.g. Initiative"
            className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Color</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-5 h-5 rounded-full border-2 transition-all ${color === c ? 'border-[var(--rule)] scale-125' : 'border-transparent'}`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description</label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of when to use this category"
          className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:outline-none"
        />
      </div>

      {/* Altitude labels */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
          Type names <span className="font-normal text-[var(--text-ter)]">(appear in the Item Types grid, optional)</span>
        </label>
        <div className="grid grid-cols-5 gap-2">
          {ALTITUDE_LEVELS.map((lvl) => (
            <div key={lvl}>
              <label className="block text-xs text-[var(--text-ter)] mb-1 text-center">Level {lvl}</label>
              <input
                type="text"
                value={altLabels[lvl]}
                onChange={(e) => setAltLabels((prev) => ({ ...prev, [lvl]: e.target.value }))}
                placeholder={`Alt ${lvl}`}
                className="w-full px-2 py-1 text-xs border border-[var(--rule)] rounded focus:ring-1 focus:ring-primary-500 focus:outline-none text-center bg-[var(--bg-raised)]"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-1">Level 5 = most strategic · Level 1 = most tactical</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleAdd}
          disabled={!label.trim() || saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          {saving ? 'Creating…' : 'Create'}
        </button>
        <button
          onClick={() => { reset(); setOpen(false); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)]"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function ItemCategories() {
  const workspaceId = getCurrentWorkspaceId();
  const addNotification = useUIStore((s) => s.addNotification);
  const { categories, loading, error, fetchCategories, toggleActive, addCategory, editCategory, removeCategory } =
    useItemCategoriesStore();

  useEffect(() => {
    if (workspaceId) fetchCategories(workspaceId);
  }, [workspaceId, fetchCategories]);

  const handleToggle = async (id: string, isActive: boolean) => {
    await toggleActive(id, isActive);
    addNotification({
      type: 'success',
      title: isActive ? 'Category activated' : 'Category deactivated',
      message: `The category is now ${isActive ? 'available' : 'hidden'} across the workspace.`,
      duration: 3000,
    });
  };

  const handleDelete = async (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat || cat.isBuiltin) return;
    await removeCategory(id);
    addNotification({
      type: 'success',
      title: 'Category deleted',
      message: `"${cat.label}" has been removed.`,
      duration: 3000,
    });
  };

  const handleAdd = async (
    wsId: string,
    label: string,
    color: string | null,
    description: string | null,
    altitudeLabels: Record<AltLevel, string | null>
  ) => {
    const result = await addCategory(wsId, label, color, description, altitudeLabels);
    if (result) {
      addNotification({
        type: 'success',
        title: 'Category created',
        message: `"${result.label}" has been added. Use Labels or Quiz to further configure it.`,
        duration: 5000,
      });
    }
    return result;
  };

  const handleSaveLabels = async (id: string, labels: Record<AltLevel, string | null>) => {
    await editCategory(id, { altitudeLabels: labels });
  };

  const activeCount = categories.filter((c) => c.isActive).length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<LayoutGrid className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Item Categories"
        subtitle="Toggle built-in item categories on/off and add custom ones. Each category can have its own 3-question altitude quiz."
      />

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">Select a workspace in the sidebar to manage its item categories.</p>
        </div>
      )}

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      {workspaceId && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">Categories</h3>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {activeCount} of {categories.length} active
              </p>
            </div>
          </div>

          {loading && categories.length === 0 ? (
            <div className="text-sm text-[var(--text-ter)] py-4 text-center">Loading categories…</div>
          ) : (
            <div className="space-y-2">
              {categories.map((cat) => (
                <CategoryRow
                  key={cat.id}
                  category={cat}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  onSaveLabels={handleSaveLabels}
                />
              ))}
            </div>
          )}

          <div className="pt-2 border-t border-[var(--rule)]">
            <AddCategoryForm workspaceId={workspaceId} onAdd={handleAdd} />
          </div>
        </div>
      )}

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-1">
        <p className="text-sm text-[var(--text-sec)]">
          <strong>Tip:</strong> Builtin categories can be deactivated but not deleted. When a category is deactivated,
          it disappears from item creation forms, the track setup, and frame selectors across the workspace.
        </p>
        <p className="text-sm text-[var(--text-sec)]">
          Click <strong>Quiz</strong> on any category to edit its 3-question altitude-leveling quiz (one question per
          dimension: Time Horizon, Work Size, Impact Surface). Each question has 5 answer options from tactical to
          strategic.
        </p>
      </div>
    </div>
  );
}
